import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { EmptyState, Loading } from "@lopie/kernel-ui";

import { api } from "../lib/oncall-api.ts";
import { useDirectory } from "../lib/people.ts";

/**
 * Setting the pager up, from the pager.
 *
 * `createSchedule`, `createPolicy` and `createService` have been in the API
 * client since the port and no screen called them, so every instance ever
 * built was configured by somebody posting JSON at it with a token they
 * fished out of `sessionStorage`. An app that has to be configured by its
 * author is not an app a customer can install, and installing is the one
 * thing this platform is for.
 *
 * ## Three steps, in this order, because the order is a dependency
 *
 * A service points at a policy; a policy's levels point at rotations; a
 * rotation names people the directory already holds. Each step below is
 * offered only once the thing under it exists, and where it is not offered it
 * says which step is missing rather than presenting a form that would be
 * refused. The API refuses anyway — `no such escalation policy, or it has no
 * levels` is a 400 from `POST /oncall/services` — and that refusal is
 * rendered if it happens; this is the same fact, said a step earlier.
 *
 * ## What this screen does not do
 *
 * It creates. It does not edit or delete, and that is a real limit rather
 * than a stage of completion: a rotation somebody is being paged through is
 * not a thing to offer a Delete button for before there is any notion of who
 * changed it and when. Correcting a rotation today is still a `PUT`.
 *
 * Names come from the directory, never from an id — through the same
 * `useDirectory` the other two screens use, because this screen grew a
 * private copy of it first and a second answer to "who is this" is a second
 * answer to it (LO-107).
 */
export default function Setup() {
  const directory = useDirectory();
  const [state, setState] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = async () => {
    // One read of everything, because the steps below are about each other:
    // whether step two can be offered is a fact about step one's result.
    const [schedules, policies, services] = await Promise.all([
      api.schedules(), api.policies(), api.services(),
    ]);
    const rotations = schedules.schedules ?? [];
    // Members, per rotation, so a level can say who it would actually reach.
    // A rotation that resolves to nobody is the failure this screen is meant
    // to prevent somebody building.
    const members = {};
    for (const rotation of rotations) {
      try {
        members[rotation.id] = (await api.schedule(rotation.id)).members ?? [];
      } catch {
        // One rotation failing to resolve must not blank the screen.
      }
    }
    setState({
      rotations,
      members,
      policies: policies.policies ?? [],
      services: services.services ?? [],
    });
  };

  useEffect(() => {
    load().catch((e) => setFailure(e.message));
  }, []);

  if (failure) {
    return (
      <Alert severity="error" variant="outlined">
        <AlertTitle>Could not read this instance's setup</AlertTitle>
        {failure}
      </Alert>
    );
  }
  if (!state || directory.loading) {
    return <Loading label="Reading what is set up" />;
  }

  const people = directory.people;
  const nameOf = directory.nameOf;

  const ready = state.services.length > 0
    && state.policies.some((p) => (p.levels ?? []).length > 0)
    && state.rotations.length > 0;

  return (
    <Stack spacing={2}>
      <Alert severity={ready ? "success" : "info"} variant="outlined">
        <AlertTitle>
          {ready
            ? "This pager can be paged"
            : "This pager cannot be paged yet"}
        </AlertTitle>
        <Typography variant="body2">
          A monitor opens an incident by posting to <code>/oncall/intake</code>{" "}
          with a service's routing key. That reaches somebody only if the
          service points at an escalation policy, the policy has levels, and
          the levels point at rotations with people on them — which is the
          order of the three steps below.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          It says nothing about whether anyone will answer. Being reachable is
          a phone number in the directory, and{" "}
          <strong>You</strong> is the screen that shows yours.
        </Typography>
      </Alert>

      <Rotations state={{ ...state, people }} nameOf={nameOf} onDone={load} />
      <Policies state={{ ...state, people }} nameOf={nameOf} onDone={load} />
      <Services state={state} onDone={load} />
    </Stack>
  );
}

/** A step's frame: a number, a name, what exists, and the form. */
function Step({ number, title, blurb, count, blocked, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="overline" color="text.secondary">
          Step {number}
        </Typography>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          // The word, not the colour alone. A count of zero is the whole
          // state of this step and reading it should not need a palette.
          label={count === 0 ? "none yet" : `${count} set up`}
          sx={{ color: count === 0 ? "kernel.attention" : "kernel.permitted" }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {blurb}
      </Typography>
      <Divider sx={{ my: 2 }} />
      {blocked ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Not yet</AlertTitle>
          <Typography variant="body2">{blocked}</Typography>
        </Alert>
      ) : (
        children
      )}
    </Paper>
  );
}

/** Shared submit handling, so every form reports a refusal the same way. */
function useSubmit(onDone) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const run = async (work) => {
    setBusy(true);
    setProblem(null);
    try {
      const result = await work();
      await onDone();
      return result;
    } catch (e) {
      // The API's own words. `no such escalation policy, or it has no levels`
      // names the thing that is wrong; a sentence written here would be a
      // second, worse description of a decision this screen did not make.
      setProblem(e.body?.detail ? `${e.message} — ${e.body.detail}` : e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const banner = problem ? (
    <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
      <AlertTitle>Not saved</AlertTitle>
      {problem}
    </Alert>
  ) : null;
  return { busy, run, banner };
}

// ---------------------------------------------------------------------------
// Step one
// ---------------------------------------------------------------------------

function Rotations({ state, nameOf, onDone }) {
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState([]);
  const [pick, setPick] = useState("");
  const [timezone, setTimezone] = useState(
    // The browser's, because it is right far more often than a default is and
    // is visible in the field for the times it is not.
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [turnDays, setTurnDays] = useState(7);
  const { busy, run, banner } = useSubmit(onDone);

  const remaining = state.people.filter((p) => !chosen.includes(p.id));
  const move = (index, by) => {
    const next = [...chosen];
    const to = index + by;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setChosen(next);
  };

  const withoutPhone = chosen.filter(
    (id) => !(state.people.find((p) => p.id === id)?.phones ?? []).length);

  const submit = () => run(async () => {
    await api.createSchedule({
      name: name.trim() || "Primary",
      rotationType: "weekly",
      turnDays: Number(turnDays) || 7,
      timezone,
      handoffWeekday: 1,
      handoffHour: 9,
      handoffMinute: 0,
      members: chosen,
    });
    setName("");
    setChosen([]);
  });

  return (
    <Step
      number={1}
      title="Rotations"
      blurb="Who can be paged, and in what order. People come from your directory — this app keeps no list of its own."
      count={state.rotations.length}
    >
      {state.rotations.length === 0 ? (
        <EmptyState title="No rotations yet" sx={{ mb: 2 }}>
          <Typography variant="body2">
            An escalation level pointed at a rotation that does not exist
            escalates past in silence. Make one before the incident does.
          </Typography>
        </EmptyState>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {state.rotations.map((rotation) => (
            <Stack
              key={rotation.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography sx={{ flexGrow: 1 }}>{rotation.name}</Typography>
              {(state.members[rotation.id] ?? []).length === 0 ? (
                <Typography variant="body2" sx={{ color: "kernel.refused" }}>
                  Nobody on it — a page routed here reaches no one
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {(state.members[rotation.id] ?? []).map(nameOf).join(" → ")}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {state.people.length === 0 ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>The directory has nobody in it</AlertTitle>
          <Typography variant="body2">
            This app reads people tagged <code>employee</code> from the kernel.
            Until a connector has synced some, there is nobody to put on a
            rotation — and that is a fact about the directory rather than about
            this app's permission, which is declared and in force.
          </Typography>
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            <TextField
              label="Name it"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Primary"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Time zone"
              size="small"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              helperText="When the handoff happens"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Days each turn"
              size="small"
              type="number"
              value={turnDays}
              onChange={(e) => setTurnDays(e.target.value)}
              sx={{ width: 140 }}
            />
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
              In order
            </Typography>
            {chosen.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nobody yet. The order is the order they are paged in.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {chosen.map((id, index) => (
                  <Stack
                    key={id}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <Typography sx={{ flexGrow: 1 }}>
                      {index + 1}. {nameOf(id)}
                    </Typography>
                    <Button size="small" onClick={() => move(index, -1)}
                            disabled={index === 0}>
                      Up
                    </Button>
                    <Button size="small" onClick={() => move(index, 1)}
                            disabled={index === chosen.length - 1}>
                      Down
                    </Button>
                    <Button size="small" color="inherit"
                            onClick={() => setChosen(chosen.filter((c) => c !== id))}>
                      Remove
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              select
              size="small"
              label="Add somebody"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              sx={{ minWidth: 260 }}
              disabled={remaining.length === 0}
            >
              {remaining.map((person) => (
                <MenuItem key={person.id} value={person.id}>
                  {person.displayName}
                  {(person.phones ?? []).length ? "" : " — no number"}
                </MenuItem>
              ))}
            </TextField>
            <Button
              onClick={() => { setChosen([...chosen, pick]); setPick(""); }}
              disabled={!pick}
            >
              Add
            </Button>
          </Stack>

          {withoutPhone.length ? (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>
                {withoutPhone.length === 1
                  ? "One of these people has no number"
                  : `${withoutPhone.length} of these people have no number`}
              </AlertTitle>
              <Typography variant="body2">
                {withoutPhone.map(nameOf).join(", ")} can be put on this
                rotation and cannot be reached by it. A page to somebody with
                no number is recorded as skipped rather than failed, and the
                ladder carries on to the next person — so this is worth fixing
                and is not a reason to stop here. Each of them can state a
                number on <strong>You</strong>.
              </Typography>
            </Alert>
          ) : null}

          <Box>
            <Button
              variant="contained"
              onClick={submit}
              disabled={busy || chosen.length === 0}
            >
              Create rotation
            </Button>
          </Box>
        </Stack>
      )}
      {banner}
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step two
// ---------------------------------------------------------------------------

function Policies({ state, nameOf, onDone }) {
  const [name, setName] = useState("");
  const [levels, setLevels] = useState([{ rotationId: "", delayMinutes: 5 }]);
  const { busy, run, banner } = useSubmit(onDone);

  const set = (index, patch) =>
    setLevels(levels.map((l, n) => (n === index ? { ...l, ...patch } : l)));

  const submit = () => run(async () => {
    await api.createPolicy({
      name: name.trim() || "Default escalation",
      levels: levels
        .filter((l) => l.rotationId)
        .map((l, index) => ({
          level: index + 1,
          delayMinutes: Number(l.delayMinutes) || 5,
          targets: [{ targetType: "schedule", targetId: l.rotationId }],
        })),
    });
    setName("");
    setLevels([{ rotationId: "", delayMinutes: 5 }]);
  });

  return (
    <Step
      number={2}
      title="Escalation policies"
      blurb="A ladder: page this rotation, wait, then the next. An incident nobody acknowledges climbs it on its own."
      count={state.policies.length}
      blocked={state.rotations.length === 0
        ? "A level pages a rotation, so there has to be one first. Step 1."
        : null}
    >
      {state.policies.length === 0 ? (
        <EmptyState title="No policies yet" sx={{ mb: 2 }}>
          <Typography variant="body2">
            A service with no policy opens incidents and pages nobody.
          </Typography>
        </EmptyState>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {state.policies.map((policy) => (
            <Box key={policy.id}>
              <Typography>{policy.name}</Typography>
              {(policy.levels ?? []).length === 0 ? (
                <Typography variant="body2" sx={{ color: "kernel.refused" }}>
                  No levels. An incident routed here opens and pages nobody,
                  and a service cannot be pointed at it.
                </Typography>
              ) : (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {[...(policy.levels ?? [])]
                    .sort((a, b) => a.level - b.level)
                    .map((level) => (
                      <Typography
                        key={level.level}
                        variant="body2"
                        color="text.secondary"
                      >
                        {level.level}. page{" "}
                        {(level.targets ?? []).map((t) =>
                          t.targetType === "schedule"
                            ? (state.rotations.find((r) => r.id === t.targetId)?.name
                               ?? "a rotation that no longer exists")
                            : nameOf(t.targetId)).join(", ")}
                        {" "}· wait {level.delayMinutes} min for an answer
                      </Typography>
                    ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Stack spacing={2}>
        <TextField
          label="Name it"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Default escalation"
          sx={{ minWidth: 240, maxWidth: 360 }}
        />

        {levels.map((level, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", alignItems: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              Level {index + 1}: page
            </Typography>
            <TextField
              select
              size="small"
              label="Rotation"
              value={level.rotationId}
              onChange={(e) => set(index, { rotationId: e.target.value })}
              sx={{ minWidth: 220 }}
            >
              {state.rotations.map((rotation) => (
                <MenuItem key={rotation.id} value={rotation.id}>
                  {rotation.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Wait (minutes)"
              value={level.delayMinutes}
              onChange={(e) => set(index, { delayMinutes: e.target.value })}
              sx={{ width: 150 }}
            />
            {levels.length > 1 ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => setLevels(levels.filter((_, n) => n !== index))}
              >
                Remove
              </Button>
            ) : null}
          </Stack>
        ))}

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button
            onClick={() => setLevels([...levels, { rotationId: "", delayMinutes: 10 }])}
          >
            Add a level
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || !levels.some((l) => l.rotationId)}
          >
            Create policy
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          The last level is the end of the ladder. When it goes unanswered the
          incident is recorded as <strong>exhausted</strong> and nothing
          further happens to it on its own — which is said out loud on the
          incident, because a page that goes quiet otherwise looks like one
          that was handled.
        </Typography>
      </Stack>
      {banner}
    </Step>
  );
}

// ---------------------------------------------------------------------------
// Step three
// ---------------------------------------------------------------------------

function Services({ state, onDone }) {
  const [name, setName] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [issued, setIssued] = useState(null);
  const { busy, run, banner } = useSubmit(onDone);

  // A policy with no levels is refused by the API, so it is not offered.
  const usable = state.policies.filter((p) => (p.levels ?? []).length > 0);

  const submit = () => run(async () => {
    const result = await api.createService({ name: name.trim() || "service", policyId });
    setIssued(result);
    setName("");
    setPolicyId("");
  });

  return (
    <Step
      number={3}
      title="Services"
      blurb="What a monitor calls the thing that broke. Each one gets a routing key, and the key is how an alarm gets in without signing in."
      count={state.services.length}
      blocked={usable.length === 0
        ? state.policies.length === 0
          ? "A service routes alarms to an escalation policy, so there has to be one first. Step 2."
          : "Every policy here has no levels, and a service pointed at one would page nobody. Give a policy a level first."
        : null}
    >
      {state.services.length === 0 ? (
        <EmptyState title="No services yet" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Nothing can post to <code>/oncall/intake</code> until one exists —
            a routing key is what identifies the caller, and there is no other
            way in.
          </Typography>
        </EmptyState>
      ) : (
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {state.services.map((service) => (
            <Stack
              key={service.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography sx={{ flexGrow: 1 }}>{service.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {state.policies.find((p) => p.id === service.policyId)?.name
                  ?? "a policy that no longer exists"}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {issued ? (
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          <AlertTitle>{issued.service?.name} — its routing key</AlertTitle>
          <Typography
            component="pre"
            sx={{ overflowX: "auto", fontFamily: "monospace", my: 1 }}
          >
            {issued.routingKey}
          </Typography>
          {/* The API's own words, not a restatement. A caption written here
              would be a second description of a guarantee this screen does
              not make, and wrong the first time the rule changed. */}
          {(issued.does_not_prove ?? []).map((line) => (
            <Typography key={line} variant="body2">{line}</Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 1 }}>
            A monitor sends it as <code>routingKey</code> in the body, or as an{" "}
            <code>x-routing-key</code> header, to{" "}
            <code>POST /oncall/intake</code> — with no token of any kind.
          </Typography>
        </Alert>
      ) : null}

      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        <TextField
          label="Name it"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Checkout"
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label="Escalation policy"
          value={policyId}
          onChange={(e) => setPolicyId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {usable.map((policy) => (
            <MenuItem key={policy.id} value={policy.id}>
              {policy.name} — {(policy.levels ?? []).length} level
              {(policy.levels ?? []).length === 1 ? "" : "s"}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={submit} disabled={busy || !policyId}>
          Create service
        </Button>
      </Stack>
      {banner}
    </Step>
  );
}
